import {Box3, Group, Mesh, Object3D, Scene, Vector2, Vector3} from 'three';

import {
  checkExhaustive,
  checkExists,
} from 'external/dev_april_corgi+/js/common/asserts';
import {getFirstElement} from 'external/dev_april_corgi+/js/common/collections';
import {clamp} from 'external/dev_april_corgi+/js/common/math';

import {
  Graph,
  Link,
  Listener as GraphListener,
  Node,
  Pad,
  Patch,
  elementType,
  padsForSubgraphNode,
} from './execution/graph';
import {plateIdToLabel} from './execution/lab_state';
import {
  FUCHSIA_400_RGBA,
  GREEN_400_RGBA,
  NEUTRAL_200_RGBA,
  NEUTRAL_300_RGBA,
  NEUTRAL_500_RGBA,
  NEUTRAL_600_RGBA,
  NEUTRAL_700_RGBA,
  NEUTRAL_800_RGBA,
  RED_400_RGBA,
  RED_600_RGBA,
  RgbaU32,
  TEAL_300_RGBA,
  VIOLET_400_RGBA,
  ACCENT_600_RGBA,
  ACCENT_700_RGBA,
  TRANSPARENT_RGBA,
} from './types/colors';
import {
  BooleanValue,
  DataType,
  DataValue,
  DurationType,
  DurationValue,
  FloatType,
  FloatValue,
  IntType,
  IntValue,
  PlateType,
  PlateValue,
  MaterialType,
  MaterialValue,
  ScriptType,
  ScriptValue,
  SelectType,
  SelectValue,
  StringType,
  StringValue,
  TableType,
  TableValue,
  TemperatureType,
  TemperatureValue,
  VolumeType,
  VolumeValue,
} from './types/data';
import {
  DURATION_IDS,
  DURATION_LABELS,
  VOLUME_IDS,
  VOLUME_LABELS,
  nlToUnit,
  unitToNanos,
  usToUnit,
} from './types/units';

import {
  ObjectTree,
  layout,
  addAndPruneMeshes,
  removeAndDispose,
} from './layout';
import {deserializeFloat} from './parsing';
import {Circle, Corner, DropShadow, RoundedRect, Spline} from './shaders';
import {serialize} from './table_editor_controller';
import {capitalize, TextMesh, TextRenderer} from './text_renderer';
import {
  Z_BACKGROUND,
  Z_BORDER,
  Z_CONTROL,
  Z_FOREGROUND,
  Z_LINK,
  Z_SHADOW,
} from './z';

export const TYPE_IGNORE = 'ignore';
export const TYPE_LINK = 'link';
const TYPE_LINK_DELETE = 'link_delete';
export const TYPE_NODE = 'node';
const TYPE_NODE_HEADER = 'node_header';
export const TYPE_PAD = 'pad';
const TYPE_CHECKBOX = 'checkbox';
const TYPE_DURATION = 'duration';
const TYPE_FLOAT = 'float';
const TYPE_INT = 'int';
const TYPE_MATERIAL = 'material';
const TYPE_PLATE = 'plate';
const TYPE_SCRIPT = 'script';
const TYPE_SELECT_DROPDOWN = 'select_dropdown';
const TYPE_SELECT_RADIO = 'select_radio';
const TYPE_SELECT_RADIO_GROUP = 'select_radio_group';
const TYPE_STRING = 'string';
const TYPE_TABLE = 'table';
const TYPE_TEMPERATURE = 'temperature';
const TYPE_VOLUME = 'volume';

interface LinkHandle {
  kind: typeof TYPE_LINK;
  link: Link;
  input: PadHandle<'input'>;
  output: PadHandle<'output'>;
  objects: {
    deleteLabel: TextMesh;
    line: Spline;
  };
}

interface LinkDeleteHandle {
  kind: typeof TYPE_LINK_DELETE;
  parent: LinkHandle;
  onClick: (count: number) => Promise<void>;
}

export interface PadHandle<T extends 'input' | 'output'> {
  kind: typeof TYPE_PAD;
  node: NodeHandle;
  pad: Pad<T>;
  widget: Widget | undefined;
  links: Map<string, LinkHandle>;
  objects: {
    display: Circle;
    target: Circle;
    warning: TextMesh;
    label: {
      label: string;
      mesh: TextMesh;
    };
  };
}

interface NodeHandle {
  kind: typeof TYPE_NODE;
  node: Node;
  inputs: Map<string, PadHandle<'input'>>;
  outputs: Map<string, PadHandle<'output'>>;
  values: Map<string, Widget>;
  containedMeshes: Set<Mesh>;
  objects: {
    activeOutline: RoundedRect;
    background: RoundedRect;
    headerBackground: RoundedRect;
    group: Group;
    shadow: DropShadow;
    headerTitle: {
      label: string;
      mesh: TextMesh;
    };
  };
}

interface NodeHeaderHandle {
  kind: typeof TYPE_NODE_HEADER;
  parent: NodeHandle;
  onClick: (count: number) => Promise<void>;
}

interface CheckboxWidget {
  kind: typeof TYPE_CHECKBOX;
  onClick: (count: number) => Promise<void>;
  onEnter: () => void;
  onLeave: () => void;
  box: RoundedRect;
  tick: TextMesh;
  label: {label: string; mesh: TextMesh};
}

interface DurationWidget {
  kind: typeof TYPE_DURATION;
  number: {
    widget: StringWidget;
    tree: ObjectTree;
    value: StringValue | undefined;
  };
  unit: {
    widget: SelectDropdownWidget;
    tree: ObjectTree;
    value: SelectValue | undefined;
  };
}

interface FloatWidget {
  kind: typeof TYPE_FLOAT;
  number: {
    widget: StringWidget;
    tree: ObjectTree;
    value: StringValue | undefined;
  };
}

interface IntWidget {
  kind: typeof TYPE_INT;
  number: {
    widget: StringWidget;
    tree: ObjectTree;
    value: StringValue | undefined;
  };
}

interface PlateWidget {
  kind: typeof TYPE_PLATE;
  onClick: (count: number) => Promise<void>;
  plate: {
    widget: SelectDropdownWidget;
    tree: ObjectTree;
    value: SelectValue | undefined;
  };
}

interface ScriptWidget {
  kind: typeof TYPE_SCRIPT;
  handle: PadHandle<'input'>;
  onClick: (count: number) => Promise<void>;
  onEnter: () => void;
  onLeave: () => void;
  background: RoundedRect;
  label: {label: string; mesh: TextMesh};
  value: {label: string; mesh: TextMesh};
}

interface SelectDropdownWidget {
  kind: typeof TYPE_SELECT_DROPDOWN;
  onClick: (count: number) => Promise<void>;
  onEnter: () => void;
  onLeave: () => void;
  background: RoundedRect;
  arrow: TextMesh;
  label: {label: string; mesh: TextMesh};
  value: {label: string; mesh: TextMesh};
}

interface SelectRadioWidget {
  kind: typeof TYPE_SELECT_RADIO_GROUP;
  options: SelectRadio[];
}

interface SelectRadio {
  kind: typeof TYPE_SELECT_RADIO;
  onClick: (count: number) => Promise<void>;
  onEnter: () => void;
  onLeave: () => void;
  background: RoundedRect;
  option: string | undefined;
  mesh: TextMesh;
}

interface StringWidget {
  kind: typeof TYPE_STRING;
  onClick: (count: number) => Promise<void>;
  onEnter: () => void;
  onLeave: () => void;
  onSave: (value: string) => Promise<void>;
  background: RoundedRect;
  label: {label: string; mesh: TextMesh};
  value: {label: string; mesh: TextMesh};
}

interface TableWidget {
  kind: typeof TYPE_TABLE;
  handle: PadHandle<'input'>;
  onClick: (count: number) => Promise<void>;
  onEnter: () => void;
  onLeave: () => void;
  background: RoundedRect;
  label: {label: string; mesh: TextMesh};
  value: {label: string; mesh: TextMesh};
}

interface TemperatureWidget {
  kind: typeof TYPE_TEMPERATURE;
  number: {
    widget: StringWidget;
    tree: ObjectTree;
    value: StringValue | undefined;
  };
  unit: {label: string; mesh: TextMesh};
}

interface VolumeWidget {
  kind: typeof TYPE_VOLUME;
  number: {
    widget: StringWidget;
    tree: ObjectTree;
    value: StringValue | undefined;
  };
  unit: {
    widget: SelectDropdownWidget;
    tree: ObjectTree;
    value: SelectValue | undefined;
  };
}

interface MaterialWidget {
  kind: typeof TYPE_MATERIAL;
  onClick: (count: number) => Promise<void>;
  material: {
    widget: SelectDropdownWidget;
    tree: ObjectTree;
    value: SelectValue | undefined;
  };
}

type Widget =
  | CheckboxWidget
  | DurationWidget
  | FloatWidget
  | IntWidget
  | MaterialWidget
  | PlateWidget
  | ScriptWidget
  | SelectDropdownWidget
  | SelectRadioWidget
  | StringWidget
  | TableWidget
  | TemperatureWidget
  | VolumeWidget;

export type ObjectHandle =
  | PadHandle<'input' | 'output'>
  | LinkHandle
  | LinkDeleteHandle
  | NodeHandle
  | NodeHeaderHandle
  | CheckboxWidget
  | ScriptWidget
  | SelectDropdownWidget
  | SelectRadio
  | StringWidget
  | TableWidget
  | TemperatureWidget;

interface Listener {
  deleteLink(link: Link): Promise<void>;
  openDurationNumberEditor(
    pad: Pad<'input'>,
    position: Vector3,
    bound: Box3,
  ): void;
  openDurationUnitEditor(
    pad: Pad<'input'>,
    position: Vector3,
    bound: Box3,
  ): void;
  openFloatEditor(pad: Pad<'input'>, position: Vector3, bound: Box3): void;
  openIntEditor(pad: Pad<'input'>, position: Vector3, bound: Box3): void;
  openNodeEditor(node: Node): void;
  openPlateEditor(pad: Pad<'input'>, position: Vector3, bound: Box3): void;
  openScriptEditor(pad: Pad<'input'>, position: Vector3, bound: Box3): void;
  openSelectEditor(pad: Pad<'input'>, position: Vector3, bound: Box3): void;
  openStringEditor(pad: Pad<'input'>, position: Vector3, bound: Box3): void;
  openTableEditor(pad: Pad<'input'>, position: Vector3, bound: Box3): void;
  openTemperatureEditor(
    pad: Pad<'input'>,
    position: Vector3,
    bound: Box3,
  ): void;
  openVolumeNumberEditor(
    pad: Pad<'input'>,
    position: Vector3,
    bound: Box3,
  ): void;
  openVolumeUnitEditor(pad: Pad<'input'>, position: Vector3, bound: Box3): void;
  openMaterialContentEditor(
    pad: Pad<'input'>,
    position: Vector3,
    bound: Box3,
  ): void;
}

export const GRID_UNITS = 4; /* px / unit */
const PAD_RADIUS = 5;
const SPACING = new Object3D();

const DATA_TYPE_COLORS: Partial<{[K in DataType['kind']]: RgbaU32}> = {
  any: NEUTRAL_200_RGBA,
  int: GREEN_400_RGBA,
  plate: FUCHSIA_400_RGBA,
  plated_material: TEAL_300_RGBA,
  string: VIOLET_400_RGBA,
  temperature: RED_400_RGBA,
} as const;

const Z_SEPARATION = 10;

export class SceneGraph implements GraphListener {
  readonly scene: Scene;
  private readonly objects: WeakMap<Object3D, ObjectHandle>;
  private readonly nodes: WeakMap<Node, NodeHandle>;
  private readonly pads: WeakMap<
    Pad<'input' | 'output'>,
    PadHandle<'input' | 'output'>
  >;
  private readonly textRenderer: TextRenderer;
  private _focused: Widget | undefined;
  private _hovered: ObjectHandle | undefined;
  private _selected: Set<NodeHandle>;
  // TODO(april): okay doing this in ThreeJS was a mistake. But now that we're here...
  rerender: boolean;
  private top: number;

  constructor(
    private readonly graph: Graph,
    private readonly listener: Listener,
  ) {
    this.scene = new Scene();
    this.objects = new WeakMap();
    this.nodes = new WeakMap();
    this.pads = new WeakMap();
    this.textRenderer = new TextRenderer();
    this._focused = undefined;
    this._hovered = undefined;
    this._selected = new Set();
    this.rerender = false;
    this.top = 10;
  }

  get focused(): Widget | undefined {
    return this._focused;
  }

  set focused(handle: Widget | undefined) {
    if (this._focused && 'background' in this._focused) {
      this._focused.background.stroke = NEUTRAL_500_RGBA;
    }
    this._focused = handle;
    if (this._focused && 'background' in this._focused) {
      this._focused.background.stroke = NEUTRAL_300_RGBA;
    }
  }

  get hovered(): ObjectHandle | undefined {
    return this._hovered;
  }

  set hovered(handle: ObjectHandle | undefined) {
    if (handle === this._hovered) {
      return;
    }

    if (this._hovered?.kind === TYPE_LINK) {
      this._hovered.objects.deleteLabel.visible = false;
    } else if (this._hovered?.kind === TYPE_LINK_DELETE) {
      this._hovered.parent.objects.deleteLabel.visible = false;
    }
    this._hovered = handle;
    if (this._hovered?.kind === TYPE_LINK) {
      this._hovered.objects.deleteLabel.visible = true;
    } else if (this._hovered?.kind === TYPE_LINK_DELETE) {
      this._hovered.parent.objects.deleteLabel.visible = true;
    }
  }

  get selected(): Set<NodeHandle> {
    return this._selected;
  }

  clearSelection(): void {
    for (const selected of this._selected) {
      selected.objects.activeOutline.visible = false;
    }
    this._selected.clear();
  }

  selectAdditive(handle: NodeHandle): void {
    this._selected.add(handle);
    handle.objects.activeOutline.visible = true;
    handle.objects.group.position.z = this.top * Z_SEPARATION;
    this.top += 1;
    // TODO(april): reset top
  }

  selectOnly(handle: NodeHandle): void {
    this.clearSelection();
    this.selectAdditive(handle);
  }

  intersectNodes(box: Box3): NodeHandle[] {
    const nodes = [];
    const bounds = new Box3();
    for (const child of this.scene.children) {
      const handle = this.objects.get(child);
      if (handle?.kind === TYPE_NODE) {
        if (bounds.setFromObject(child).intersectsBox(box)) {
          nodes.push(handle);
        }
      }
    }
    return nodes;
  }

  async patch(patches: Patch[]): Promise<void> {
    for (const patch of patches) {
      if (patch.kind === 'add_node') {
        if (patch.node.subgraphId) {
          continue;
        }

        const handle = await this.patchNode(undefined, patch.node);
        handle.objects.group.position.x = patch.position.x;
        handle.objects.group.position.y = patch.position.y;
        handle.objects.group.position.z = this.top * Z_SEPARATION;
        this.top += 1;
        handle.objects.group.scale.z = 1 / Z_SEPARATION;
        this.nodes.set(patch.node, handle);
        const a = new Vector3();
        const b = new Vector3();
        for (const pads of [handle.inputs, handle.outputs]) {
          for (const pad of pads.values()) {
            this.pads.set(pad.pad, pad);

            for (const link of pad.links.values()) {
              link.objects.line.setEndpoints(
                link.output.objects.display.getWorldPosition(a),
                link.input.objects.display.getWorldPosition(b),
              );
              const midpoint = link.objects.line.midpoint;
              link.objects.deleteLabel.position.x = midpoint[0];
              link.objects.deleteLabel.position.y = midpoint[1];
            }
          }
        }
        this.objects.set(handle.objects.group, handle);
        this.scene.add(handle.objects.group);
        this.selectOnly(handle);
      } else if (patch.kind === 'add_link') {
        if (
          patch.link.output.node.subgraphId &&
          patch.link.input.node.subgraphId
        ) {
          continue;
        }

        const output = checkExists(
          this.pads.get(patch.link.output),
        ) as PadHandle<'output'>;
        const input = checkExists(
          this.pads.get(patch.link.input),
        ) as PadHandle<'input'>;
        const handle: LinkHandle = {
          kind: TYPE_LINK,
          link: patch.link,
          input,
          output,
          objects: {
            deleteLabel: await this.textRenderer.render('×', {scale: 2}),
            line: new Spline({
              fillStart: (output.objects.display.fill |
                (0 << 8) |
                0xff) as RgbaU32,
              fillEnd: (input.objects.display.fill |
                (0 << 8) |
                0xff) as RgbaU32,
              lineWidth: 2,
              name: TYPE_LINK,
              positionStart: output.objects.display.getWorldPosition(
                new Vector3(),
              ),
              positionEnd: input.objects.display.getWorldPosition(
                new Vector3(),
              ),
              z: Z_LINK,
            }),
          },
        };
        handle.objects.deleteLabel.name = TYPE_LINK_DELETE;
        const midpoint = handle.objects.line.midpoint;
        handle.objects.deleteLabel.position.x = midpoint[0];
        handle.objects.deleteLabel.position.y = midpoint[1];
        handle.objects.deleteLabel.position.z = Z_LINK + 0.1;
        handle.objects.deleteLabel.visible = false;
        this.objects.set(handle.objects.deleteLabel, {
          kind: TYPE_LINK_DELETE,
          parent: handle,
          onClick: () => this.listener.deleteLink(patch.link),
        });
        handle.objects.line.position.z = Z_LINK;
        this.objects.set(handle.objects.line, handle);
        this.scene.add(handle.objects.line);
        this.scene.add(handle.objects.deleteLabel);
        output.links.set(patch.link.id, handle);
        input.links.set(patch.link.id, handle);
      } else if (patch.kind === 'delete_link') {
        if (
          patch.link.output.node.subgraphId &&
          patch.link.input.node.subgraphId
        ) {
          continue;
        }

        const id = patch.link.id;
        const output = checkExists(this.pads.get(patch.link.output));
        const link = checkExists(output.links.get(id));
        removeAndDispose(link.objects.deleteLabel);
        removeAndDispose(link.objects.line);
        link.output.links.delete(id);
        link.input.links.delete(id);
      } else if (patch.kind === 'delete_node') {
        if (patch.node.subgraphId) {
          continue;
        }

        const handle = checkExists(this.nodes.get(patch.node));
        handle.containedMeshes.forEach(removeAndDispose);
        handle.objects.group.removeFromParent();
        this.clearSelection();
      } else if (patch.kind === 'move_node') {
        const handle = checkExists(this.nodes.get(patch.node));
        handle.objects.group.position.x = patch.position.x;
        handle.objects.group.position.y = patch.position.y;
        const a = new Vector3();
        const b = new Vector3();
        for (const pads of [handle.outputs, handle.inputs]) {
          for (const pad of pads.values()) {
            for (const link of pad.links.values()) {
              link.objects.line.setEndpoints(
                link.output.objects.display.getWorldPosition(a),
                link.input.objects.display.getWorldPosition(b),
              );
              const midpoint = link.objects.line.midpoint;
              link.objects.deleteLabel.position.x = midpoint[0];
              link.objects.deleteLabel.position.y = midpoint[1];
            }
          }
        }
      } else if (patch.kind === 'update_node') {
        if (patch.node.subgraphId) {
          continue;
        }

        const original = this.nodes.get(patch.node);
        if (!original) {
          // We haven't processed the add yet, so whatever. Note that this is a *return* so nothing
          // else will be processed from this patch.
          return;
        }
        const handle = await this.patchNode(original, patch.node);
        const a = new Vector3();
        const b = new Vector3();
        for (const pads of [handle.inputs, handle.outputs]) {
          for (const pad of pads.values()) {
            this.pads.set(pad.pad, pad);

            for (const link of pad.links.values()) {
              link.objects.line.setEndpoints(
                link.output.objects.display.getWorldPosition(a),
                link.input.objects.display.getWorldPosition(b),
              );
              const midpoint = link.objects.line.midpoint;
              link.objects.deleteLabel.position.x = midpoint[0];
              link.objects.deleteLabel.position.y = midpoint[1];
            }
          }
        }
      } else {
        throw checkExhaustive(patch);
      }
    }

    this.rerender = true;
  }

  private async patchNode(
    current: NodeHandle | undefined,
    desired: Node,
  ): Promise<NodeHandle> {
    const edgeRadius = 8;
    const handle: NodeHandle = current ?? {
      kind: TYPE_NODE,
      node: desired,
      inputs: new Map(),
      outputs: new Map(),
      values: new Map(),
      containedMeshes: new Set(),
      objects: {
        activeOutline: new RoundedRect({
          name: TYPE_IGNORE,
          size: new Vector2(0, 0),
          stroke: 0xfafafaff,
          fill: TRANSPARENT_RGBA,
          radius: edgeRadius,
          visible: false,
          z: Z_BORDER,
        }),
        background: new RoundedRect({
          size: new Vector2(0, 0),
          stroke: NEUTRAL_800_RGBA,
          fill: NEUTRAL_800_RGBA,
          radius: edgeRadius,
          z: Z_BACKGROUND,
        }),
        group: new Group(),
        shadow: new DropShadow({
          size: new Vector2(0, 0),
          radius: edgeRadius,
          z: Z_SHADOW,
        }),
        headerBackground: new RoundedRect({
          name: TYPE_NODE_HEADER,
          size: new Vector2(0, 0),
          round: Corner.TOP_LEFT | Corner.TOP_RIGHT,
          fill: desired.color,
          radius: edgeRadius,
          stroke: desired.color,
          z: Z_FOREGROUND,
        }),
        headerTitle: {label: '', mesh: await TextMesh.empty()},
      },
    };

    if (!current) {
      this.objects.set(handle.objects.headerBackground, {
        kind: TYPE_NODE_HEADER,
        parent: handle,
        onClick: async (count: number) => {
          if (count >= 2) {
            this.listener.openNodeEditor(desired);
          }
        },
      });
    }

    if (
      this.graph.overlay &&
      this.graph.overlay.errorsForNode(desired).length > 0
    ) {
      handle.objects.shadow.fill = RED_600_RGBA;
    } else {
      handle.objects.shadow.fill = undefined; // reset to the default color
    }

    if (handle.objects.headerBackground.fill !== desired.color) {
      handle.objects.headerBackground.fill = desired.color;
      handle.objects.headerBackground.stroke = desired.color;
    }

    const header: ObjectTree = {
      kind: 'tree',
      basis: {height: 0, width: 0},
      direction: 'horizontal',
      gravity: 'center',
      grow: 0,
      children: [],
      insets: [
        {
          kind: 'leaf',
          object: handle.objects.headerBackground,
          notify: size => {
            handle.objects.headerBackground.size = new Vector2(
              size.width,
              size.height,
            );
          },
          basis: {height: 0, width: 0},
          grow: 0,
        },
      ],
    };
    const tree: ObjectTree = {
      kind: 'tree',
      gap: 4,
      gravity: 'center',
      children: [header],
      insets: [
        {
          kind: 'leaf',
          object: handle.objects.activeOutline,
          notify: size => {
            handle.objects.activeOutline.size = new Vector2(
              size.width,
              size.height,
            );
          },
          basis: {height: 0, width: 0},
          grow: 0,
        },
        {
          kind: 'leaf',
          object: handle.objects.background,
          notify: size => {
            handle.objects.background.size = new Vector2(
              size.width,
              size.height,
            );
          },
          basis: {height: 0, width: 0},
          grow: 0,
        },
        {
          kind: 'leaf',
          object: handle.objects.shadow,
          notify: size => {
            handle.objects.shadow.size = new Vector2(size.width, size.height);
          },
          basis: {height: 0, width: 0},
          grow: 0,
        },
      ],
    };

    if (handle.objects.headerTitle.label !== desired.label) {
      removeAndDispose(handle.objects.headerTitle.mesh);
      handle.objects.headerTitle = {
        label: desired.label,
        mesh: await this.textRenderer.render(desired.label, {
          name: TYPE_IGNORE,
        }),
      };
    }

    {
      const titleBound = checkExists(
        handle.objects.headerTitle.mesh.geometry.boundingBox,
      );
      checkExists(header.children).push(
        {
          kind: 'leaf',
          object: SPACING,
          grow: 0,
          basis: {height: 0, width: GRID_UNITS},
        },
        {
          kind: 'leaf',
          object: SPACING,
          grow: 1,
          basis: {height: 0, width: GRID_UNITS},
        },
        {
          kind: 'leaf',
          object: handle.objects.headerTitle.mesh,
          grow: 0,
          basis: {
            height: titleBound.max.y - titleBound.min.y,
            width: titleBound.max.x - titleBound.min.x,
          },
        },
        {
          kind: 'leaf',
          object: SPACING,
          grow: 1,
          basis: {height: 0, width: GRID_UNITS},
        },
        {
          kind: 'leaf',
          object: SPACING,
          grow: 0,
          basis: {height: 0, width: GRID_UNITS},
        },
      );
    }

    let desiredInputs;
    let desiredOutputs;
    if (desired.behavior.kind === 'subgraph') {
      const pads = padsForSubgraphNode(desired, this.graph);
      desiredInputs = pads.inputs;
      desiredOutputs = pads.outputs;
    } else {
      desiredInputs = desired.inputs;
      desiredOutputs = desired.outputs;
    }

    checkExists(tree.children).push(
      await this.patchPads(handle, handle.outputs, desiredOutputs),
    );
    checkExists(tree.children).push(
      await this.patchPads(handle, handle.inputs, desiredInputs),
    );
    checkExists(tree.children).push({
      kind: 'leaf',
      object: SPACING,
      grow: 0,
      basis: {height: 0, width: GRID_UNITS},
    });
    layout(tree);
    addAndPruneMeshes(tree, handle.containedMeshes, handle.objects.group);

    return handle;
  }

  private async patchPads<T extends 'input' | 'output'>(
    node: NodeHandle,
    current: Map<string, PadHandle<T>>,
    desired: Map<string, Pad<T>>,
  ): Promise<ObjectTree> {
    const tree: ObjectTree = {
      kind: 'tree',
      gap: 2,
      gravity: 'center',
      children: [],
    };

    const unused = new Map(current);
    current.clear();
    for (const [key, pad] of desired) {
      const had = unused.get(key);
      let handle: PadHandle<T>;
      if (had) {
        handle = had;
        unused.delete(key);
      } else {
        handle = {
          kind: TYPE_PAD,
          node,
          pad,
          widget: undefined,
          links: new Map(),
          objects: {
            display: new Circle({
              fill: 0x000000ff,
              name: TYPE_IGNORE,
              radius: PAD_RADIUS,
              stroke: 0x000000ff,
              z: Z_CONTROL + 1,
            }),
            target: new Circle({
              fill: 0x00000000,
              radius: PAD_RADIUS + 3,
              stroke: 0x00000000,
              z: Z_CONTROL,
            }),
            warning: await this.textRenderer.render('⚠️', {
              name: TYPE_IGNORE,
              visible: false,
            }),
            label: {label: '', mesh: await TextMesh.empty()},
          },
        };
        this.objects.set(handle.objects.target, handle);
      }

      checkExists(tree.children).push(await this.patchPad(handle, pad));

      current.set(key, handle);
    }

    // TODO(april): make patching set up the links AFTER nodes are created

    for (const pad of unused.values()) {
      for (const [id, link] of [...pad.links.entries()]) {
        link.input.links.delete(id);
        link.output.links.delete(id);
        removeAndDispose(link.objects.deleteLabel);
        removeAndDispose(link.objects.line);
      }
    }

    return tree;
  }

  private async patchPad<T extends 'input' | 'output'>(
    current: PadHandle<T>,
    desired: Pad<T>,
  ): Promise<ObjectTree> {
    const fill =
      DATA_TYPE_COLORS[elementType(desired.type).kind] ??
      (0xffbb55ff as RgbaU32);
    if (current.objects.display.fill !== fill) {
      current.objects.display.fill = fill;
      for (const link of current.links.values()) {
        let start;
        let end;
        if (link.output === current) {
          start = fill;
          end = (link.input.objects.display.fill | (0 << 8) | 0xff) as RgbaU32;
        } else {
          start = (link.output.objects.display.fill |
            (0 << 8) |
            0xff) as RgbaU32;
          end = fill;
        }
        link.objects.line.updateFill(start, end);
      }
    }
    const errors = this.graph.overlay?.errorsForPad(desired) ?? [];
    current.objects.warning.visible = errors.length > 0;

    if (current.objects.label.label !== desired.label) {
      removeAndDispose(current.objects.label.mesh);
      current.objects.label = {
        label: desired.label,
        mesh: await this.textRenderer.render(desired.label),
      };
    }

    // If there is no data coming in, then consider plopping a widget
    let widget;
    if (current.pad.direction === 'input' && current.pad.links.size === 0) {
      const asInput = current as PadHandle<'input'>;
      const result = await this.patchValueWidget(asInput);
      if (result) {
        // We don't use Graph#setValue because
        // a) we don't want to cascade here or else we get an infinite loop
        // b) we want to preserve constraints because otherwise it's bad times. Hard to justify but
        asInput.pad.unlinked = result.value
          ? {
              value: result.value,
              constraints: asInput.pad.unlinked?.constraints ?? {},
            }
          : undefined;
        asInput.widget = result.widget;
        widget = result.tree;
      } else {
        asInput.widget = undefined;
        widget = undefined;
      }
    } else {
      current.widget = undefined;
      widget = undefined;
    }

    const bound = checkExists(current.objects.label.mesh.geometry.boundingBox);
    const tree: ObjectTree = {
      kind: 'tree',
      direction: 'horizontal',
      gravity: desired.direction === 'input' ? 'start' : 'end',
      grow: 1,
      children: [
        {
          kind: 'tree',
          // Giving ourselves 0 width means we get centered on the border
          basis: {height: 0, width: 8 * GRID_UNITS},
          direction: 'horizontal',
          gravity: 'center',
          insets: [
            {
              kind: 'leaf',
              object: current.objects.display,
            },
            {
              kind: 'leaf',
              object: current.objects.target,
            },
            {
              kind: 'leaf',
              object: current.objects.warning,
            },
          ],
        },
        {
          kind: 'leaf',
          object: SPACING,
          basis: {height: 0, width: 2 * PAD_RADIUS},
        },
        widget
          ? widget
          : {
              kind: 'leaf',
              object: current.objects.label.mesh,
              basis: {
                height: bound.max.y - bound.min.y,
                width: bound.max.x - bound.min.x,
              },
            },
        {
          kind: 'leaf',
          object: SPACING,
          basis: {height: 0, width: GRID_UNITS},
        },
      ],
    };
    if (desired.direction === 'output') {
      checkExists(tree.children).reverse();
    }
    return tree;
  }

  private async patchValueWidget(handle: PadHandle<'input'>): Promise<
    | {
        widget: Widget;
        tree: ObjectTree;
        value: DataValue | undefined;
      }
    | undefined
  > {
    const pad = handle.pad;
    if (pad.type.kind === 'boolean') {
      if (handle.widget && handle.widget.kind !== TYPE_CHECKBOX) {
        // TODO(april): do we need to worry about the value here?
        handle.widget = undefined;
      }
      const callback = async () => {
        const was = pad.unlinked?.value;
        const now = was?.kind === 'boolean' ? !was.value : true;
        await this.graph.setValue(pad, {kind: 'boolean', value: now});
      };
      const value = pad.unlinked?.value;
      if (value && value.kind !== 'boolean') {
        throw new Error('Desired value mismatches with the given type');
      }
      return await this.patchCheckboxWidget(
        pad.label,
        handle.widget,
        value,
        callback,
      );
    } else if (pad.type.kind === 'duration') {
      if (handle.widget && handle.widget.kind !== 'duration') {
        // TODO(april): do we need to worry about the value here?
        handle.widget = undefined;
      }
      const numberClick = async (position: Vector3, bound: Box3) => {
        this.listener.openDurationNumberEditor(pad, position, bound);
      };
      const numberSave = async (v: string) => {
        const was = pad.unlinked?.value;
        const preferredUnit =
          was?.kind === 'duration' ? was.preferredUnit : 's';
        const unit = unitToNanos(1, preferredUnit).number / 1000;
        const n = deserializeFloat(v);
        if (!Number.isNaN(n)) {
          await this.graph.setValue(pad, {
            kind: 'duration',
            numberUs: n * unit,
            preferredUnit,
          });
        }
      };
      const unitClick = async (position: Vector3, bound: Box3) => {
        this.listener.openDurationUnitEditor(pad, position, bound);
      };
      const value = pad.unlinked?.value;
      if (value && value.kind !== 'duration') {
        throw new Error('Desired value mismatches with the given type');
      }
      return await this.patchDurationWidget(
        pad.label + ':',
        pad.type,
        handle.widget,
        value,
        numberClick,
        numberSave,
        unitClick,
      );
    } else if (pad.type.kind === 'float') {
      if (handle.widget && handle.widget.kind !== 'float') {
        // TODO(april): do we need to worry about the value here?
        handle.widget = undefined;
      }
      const click = async (position: Vector3, bound: Box3) => {
        this.listener.openFloatEditor(pad, position, bound);
      };
      const capture = pad.type;
      const save = async (v: string) => {
        const was = pad.unlinked?.value;
        let n;
        if (v.endsWith('%')) {
          n = deserializeFloat(v);
        } else {
          // If it was originally a percent but the user drops the % sign, keep it as a percent
          const scaler =
            was?.kind === 'float' && was.preferredDisplay === 'percentage'
              ? 100
              : 1;
          n = deserializeFloat(v) / scaler;
        }
        if (v && !Number.isNaN(n)) {
          await this.graph.setValue(pad, {
            kind: 'float',
            number: clamp(
              n,
              capture.minimum ?? Number.MIN_VALUE,
              capture.maximum ?? Number.MAX_VALUE,
            ),
            preferredDisplay:
              was?.kind === 'float' ? was.preferredDisplay : 'raw',
          });
        } else {
          await this.graph.setValue(pad, undefined);
        }
      };
      const value = pad.unlinked?.value;
      if (value && value.kind !== 'float') {
        throw new Error('Desired value mismatches with the given type');
      }
      return await this.patchFloatWidget(
        pad.label + ':',
        pad.type,
        handle.widget,
        value,
        click,
        save,
      );
    } else if (pad.type.kind === 'int') {
      if (handle.widget && handle.widget.kind !== 'int') {
        // TODO(april): do we need to worry about the value here?
        handle.widget = undefined;
      }
      const click = async (position: Vector3, bound: Box3) => {
        this.listener.openIntEditor(pad, position, bound);
      };
      const capture = pad.type;
      const save = async (value: string) => {
        const n = deserializeFloat(value);
        if (value && !Number.isNaN(n)) {
          await this.graph.setValue(pad, {
            kind: 'int',
            number: clamp(
              Math.round(n),
              capture.minimum ?? Number.MIN_VALUE,
              capture.maximum ?? Number.MAX_VALUE,
            ),
          });
        } else {
          await this.graph.setValue(pad, undefined);
        }
      };
      const value = pad.unlinked?.value;
      if (value && value.kind !== 'int') {
        throw new Error('Desired value mismatches with the given type');
      }
      return await this.patchIntWidget(
        pad.label + ':',
        pad.type,
        handle.widget,
        value,
        click,
        save,
      );
    } else if (pad.type.kind === 'material') {
      if (handle.widget && handle.widget.kind !== 'material') {
        // TODO(april): do we need to worry about the value here?
        handle.widget = undefined;
      }
      const contentCallback = async (position: Vector3, bound: Box3) => {
        this.listener.openMaterialContentEditor(pad, position, bound);
      };
      const value = pad.unlinked?.value;
      if (value && value.kind !== 'material') {
        throw new Error('Desired value mismatches with the given type');
      }
      return await this.patchMaterialWidget(
        pad.label,
        pad.type,
        handle.widget,
        value,
        contentCallback,
      );
    } else if (pad.type.kind === 'plate') {
      if (handle.widget && handle.widget.kind !== TYPE_PLATE) {
        // TODO(april): do we need to worry about the value here?
        handle.widget = undefined;
      }
      const label = pad.label + ':';
      const callback = async (position: Vector3, bound: Box3) => {
        this.listener.openPlateEditor(pad, position, bound);
      };
      const value = pad.unlinked?.value;
      if (value && value.kind !== 'plate') {
        throw new Error('Desired value mismatches with the given type');
      }
      return await this.patchPlateWidget(
        label,
        pad.type,
        handle.widget,
        value,
        callback,
      );
    } else if (pad.type.kind === 'script') {
      if (handle.widget && handle.widget.kind !== 'script') {
        // TODO(april): do we need to worry about the value here?
        handle.widget = undefined;
      }
      const value = pad.unlinked?.value;
      if (value && value.kind !== 'script') {
        throw new Error('Desired value mismatches with the given type');
      }
      return await this.patchScriptWidget(
        handle,
        pad.type,
        handle.widget,
        value,
      );
    } else if (pad.type.kind === 'select') {
      const value = pad.unlinked?.value;
      if (value && value.kind !== 'select') {
        throw new Error('Desired value mismatches with the given type');
      }

      if ((pad.type.nullable ? 1 : 0) + pad.type.options.length <= 3) {
        if (handle.widget && handle.widget.kind !== TYPE_SELECT_RADIO_GROUP) {
          // TODO(april): do we need to worry about the value here?
          handle.widget = undefined;
        }
        const callback = async (value: SelectValue | undefined) => {
          await this.graph.setValue(pad, value);
        };
        return await this.patchSelectRadioWidget(
          pad.type,
          handle.widget,
          value,
          callback,
        );
      } else {
        if (handle.widget && handle.widget.kind !== TYPE_SELECT_DROPDOWN) {
          // TODO(april): do we need to worry about the value here?
          handle.widget = undefined;
        }
        const label = pad.label + ':';
        const callback = async (position: Vector3, bound: Box3) => {
          this.listener.openSelectEditor(pad, position, bound);
        };
        return await this.patchSelectDropdownWidget(
          label,
          pad.type,
          pad.type.options,
          handle.widget,
          value,
          callback,
        );
      }
    } else if (pad.type.kind === 'string') {
      if (handle.widget && handle.widget.kind !== 'string') {
        // TODO(april): do we need to worry about the value here?
        handle.widget = undefined;
      }
      const value = pad.unlinked?.value;
      if (value && value.kind !== 'string') {
        throw new Error('Desired value mismatches with the given type');
      }
      const click = async (position: Vector3, bound: Box3) => {
        this.listener.openStringEditor(pad, position, bound);
      };
      const save = async (value: string) => {
        await this.graph.setValue(pad, {
          kind: 'string',
          value,
        });
      };
      return await this.patchStringWidget(
        pad.label + ':',
        pad.type,
        handle.widget,
        value,
        click,
        save,
      );
    } else if (pad.type.kind === 'table') {
      if (handle.widget && handle.widget.kind !== 'table') {
        // TODO(april): do we need to worry about the value here?
        handle.widget = undefined;
      }
      const value = pad.unlinked?.value;
      if (value && value.kind !== 'table') {
        throw new Error('Desired value mismatches with the given type');
      }
      return await this.patchTableWidget(
        handle,
        pad.type,
        handle.widget,
        value,
      );
    } else if (pad.type.kind === 'temperature') {
      if (handle.widget && handle.widget.kind !== 'temperature') {
        // TODO(april): do we need to worry about the value here?
        handle.widget = undefined;
      }
      const click = async (position: Vector3, bound: Box3) => {
        this.listener.openTemperatureEditor(pad, position, bound);
      };
      const save = async (value: string) => {
        const n = deserializeFloat(value);
        if (value && !Number.isNaN(n)) {
          await this.graph.setValue(pad, {
            kind: 'temperature',
            numberC: n,
          });
        } else {
          await this.graph.setValue(pad, undefined);
        }
      };
      const value = pad.unlinked?.value;
      if (value && value.kind !== 'temperature') {
        throw new Error('Desired value mismatches with the given type');
      }
      return await this.patchTemperatureWidget(
        pad.label + ':',
        pad.type,
        handle.widget,
        value,
        click,
        save,
      );
    } else if (pad.type.kind === 'volume') {
      if (handle.widget && handle.widget.kind !== 'volume') {
        // TODO(april): do we need to worry about the value here?
        handle.widget = undefined;
      }
      const numberClick = async (position: Vector3, bound: Box3) => {
        this.listener.openVolumeNumberEditor(pad, position, bound);
      };
      const numberSave = async (v: string) => {
        const was = pad.unlinked?.value;
        const preferredUnit = was?.kind === 'volume' ? was.preferredUnit : 'ul';
        const unit = unitToNanos(1, preferredUnit).number;
        const n = deserializeFloat(v);
        if (!Number.isNaN(n)) {
          await this.graph.setValue(pad, {
            kind: 'volume',
            numberNl: n * unit,
            preferredUnit,
          });
        }
      };
      const unitClick = async (position: Vector3, bound: Box3) => {
        this.listener.openVolumeUnitEditor(pad, position, bound);
      };
      const value = pad.unlinked?.value;
      if (value && value.kind !== 'volume') {
        throw new Error('Desired value mismatches with the given type');
      }
      return await this.patchVolumeWidget(
        pad.label + ':',
        pad.type,
        handle.widget,
        value,
        numberClick,
        numberSave,
        unitClick,
      );
    }

    return undefined;
  }

  private async patchCheckboxWidget(
    label: string,
    current: CheckboxWidget | undefined,
    desired: BooleanValue | undefined,
    callback: () => Promise<void>,
  ): Promise<{
    widget: CheckboxWidget;
    tree: ObjectTree;
    value: BooleanValue | undefined;
  }> {
    const handle = current ?? {
      kind: TYPE_CHECKBOX,
      onClick: callback,
      onEnter: () => {
        handle.box.fill = NEUTRAL_700_RGBA;
      },
      onLeave: () => {
        handle.box.fill = TRANSPARENT_RGBA;
      },
      box: new RoundedRect({
        fill: TRANSPARENT_RGBA,
        name: TYPE_STRING,
        radius: 2,
        size: new Vector2(12, 12),
        stroke: NEUTRAL_500_RGBA,
        z: Z_FOREGROUND,
      }),
      tick: await this.textRenderer.render('✓', {
        name: TYPE_IGNORE,
        scale: 0.6,
      }),
      label: {
        label,
        mesh: await this.textRenderer.render(label),
      },
    };
    this.objects.set(handle.box, handle);
    this.objects.set(handle.label.mesh, handle);

    const value = desired ? !!desired.value : undefined;
    handle.tick.visible = value ?? false;

    const boxBound = checkExists(handle.box.geometry.boundingBox);
    const labelBound = checkExists(handle.label.mesh.geometry.boundingBox);
    const tree: ObjectTree = {
      kind: 'tree',
      direction: 'horizontal',
      gravity: 'center',
      grow: 1,
      children: [
        {
          kind: 'tree',
          direction: 'vertical',
          gravity: 'center',
          grow: 1,
          children: [
            {
              kind: 'leaf',
              object: handle.box,
              basis: {
                height: boxBound.max.y - boxBound.min.y,
                width: boxBound.max.x - boxBound.min.x,
              },
            },
          ],
          insets: [
            {
              kind: 'leaf',
              object: handle.tick,
              notify: size => {
                handle.tick.size = new Vector2(size.width, size.height);
              },
              grow: 1,
            },
          ],
        },
        {
          kind: 'leaf',
          object: SPACING,
          basis: {height: 0, width: GRID_UNITS},
        },
        {
          kind: 'leaf',
          object: handle.label.mesh,
          basis: {
            height: labelBound.max.y - labelBound.min.y,
            width: labelBound.max.x - labelBound.min.x,
          },
        },
      ],
    };

    return {
      tree,
      value: value !== undefined ? {kind: 'boolean', value} : undefined,
      widget: handle,
    };
  }

  private async patchDurationWidget(
    label: string,
    type: DurationType,
    current: DurationWidget | undefined,
    desired: DurationValue | undefined,
    numberClick: (position: Vector3, bound: Box3) => Promise<void>,
    numberSave: (value: string) => Promise<void>,
    unitClick: (position: Vector3, bound: Box3) => Promise<void>,
  ): Promise<{
    widget: DurationWidget;
    tree: ObjectTree;
    value: DurationValue | undefined;
  }> {
    const handle: DurationWidget = current ?? {
      kind: TYPE_DURATION,
      number: undefined as any,
      unit: undefined as any,
    };
    const value: DurationValue = desired ?? {
      kind: 'duration',
      numberUs: 60 * 1000_000,
      preferredUnit: 's',
    };
    const displayed = usToUnit(value.numberUs, value.preferredUnit);
    handle.number = await this.patchStringWidget(
      label,
      {kind: 'string'},
      current?.number?.widget,
      {kind: 'string', value: String(displayed)},
      numberClick,
      numberSave,
    );
    handle.unit = await this.patchSelectDropdownWidget(
      '',
      {
        kind: 'select',
        nullable: false,
        options: DURATION_IDS,
      },
      DURATION_LABELS,
      // TODO(april): something spooky afoot. handle.unit.widget should be okay but actually
      // unit can be undefined due to the typecast.
      current?.unit?.widget,
      {kind: 'select', value: value.preferredUnit},
      unitClick,
    );

    const tree: ObjectTree = {
      kind: 'tree',
      direction: 'horizontal',
      gravity: 'start',
      grow: 1,
      children: [
        handle.number.tree,
        {
          kind: 'leaf',
          object: SPACING,
          basis: {height: GRID_UNITS, width: 0},
          grow: 0,
        },
        handle.unit.tree,
      ],
      insets: [],
    };

    return {
      tree,
      value,
      widget: handle,
    };
  }

  private async patchFloatWidget(
    label: string,
    type: FloatType,
    current: FloatWidget | undefined,
    desired: FloatValue | undefined,
    onClick: (position: Vector3, bound: Box3) => Promise<void>,
    onSave: (value: string) => Promise<void>,
  ): Promise<{
    widget: FloatWidget;
    tree: ObjectTree;
    value: FloatValue | undefined;
  }> {
    const handle: FloatWidget = current ?? {
      kind: TYPE_FLOAT,
      number: undefined as any,
    };

    const value = desired
      ? serialize({
          kind: 'float',
          number: desired.number,
          preferredDisplay: 'raw',
        })
      : undefined;

    // We do this outside the block above to pick up string changes
    handle.number = await this.patchStringWidget(
      label,
      {kind: 'string'},
      current?.number?.widget,
      value ? {kind: 'string', value} : undefined,
      onClick,
      onSave,
    );

    const tree: ObjectTree = {
      kind: 'tree',
      basis: {height: 0, width: 0},
      direction: 'horizontal',
      gravity: 'center',
      grow: 1,
      insets: [],
      children: [handle.number.tree],
    };

    return {
      tree,
      value: desired
        ? {
            kind: 'float',
            number: desired.number,
            preferredDisplay: desired.preferredDisplay,
          }
        : undefined,
      widget: handle,
    };
  }

  private async patchIntWidget(
    label: string,
    type: IntType,
    current: IntWidget | undefined,
    desired: IntValue | undefined,
    onClick: (position: Vector3, bound: Box3) => Promise<void>,
    onSave: (value: string) => Promise<void>,
  ): Promise<{
    widget: IntWidget;
    tree: ObjectTree;
    value: IntValue | undefined;
  }> {
    const is = desired?.number;
    const handle: IntWidget = current ?? {
      kind: TYPE_INT,
      number: undefined as any,
    };
    // We do this outside the block above to pick up string changes
    handle.number = await this.patchStringWidget(
      label,
      {kind: 'string'},
      current?.number?.widget,
      is !== undefined ? {kind: 'string', value: String(is)} : undefined,
      onClick,
      onSave,
    );

    const tree: ObjectTree = {
      kind: 'tree',
      basis: {height: 0, width: 0},
      direction: 'horizontal',
      gravity: 'center',
      grow: 1,
      insets: [],
      children: [handle.number.tree],
    };

    return {
      tree,
      value:
        is !== undefined
          ? {
              kind: 'int',
              number: is,
            }
          : undefined,
      widget: handle,
    };
  }

  private async patchPlateWidget(
    label: string,
    type: PlateType,
    current: PlateWidget | undefined,
    desired: PlateValue | undefined,
    contentCallback: (position: Vector3, bound: Box3) => Promise<void>,
  ): Promise<{
    widget: PlateWidget;
    tree: ObjectTree;
    value: PlateValue | undefined;
  }> {
    const handle: PlateWidget = current ?? {
      kind: TYPE_PLATE,
      onClick: async () => {},
      plate: undefined as any,
    };
    handle.plate = await this.patchSelectDropdownWidget(
      label,
      {
        kind: 'select',
        nullable: true,
        options: desired ? [desired.id] : [],
      },
      desired ? [plateIdToLabel(desired.id)] : [],
      // TODO(april): something spooky afoot. handle.plate.widget should be okay but actually
      // plate can be undefined due to the typecast.
      current?.plate?.widget,
      desired ? {kind: 'select', value: desired.id} : undefined,
      contentCallback,
    );

    const tree: ObjectTree = {
      kind: 'tree',
      basis: {height: 0, width: 0},
      direction: 'vertical',
      gravity: 'start',
      grow: 1,
      children: [handle.plate.tree],
      insets: [],
    };

    return {
      tree,
      value: desired?.kind === 'plate' ? desired : undefined,
      widget: handle,
    };
  }

  private async patchScriptWidget(
    pad: PadHandle<'input'>,
    type: ScriptType,
    current: ScriptWidget | undefined,
    desired: ScriptValue | undefined,
  ): Promise<{
    widget: ScriptWidget;
    tree: ObjectTree;
    value: ScriptValue | undefined;
  }> {
    const label = pad.pad.label + ':';
    const is = `(${desired?.value?.split('\n')?.length ?? 0} lines)`;
    const handle = current ?? {
      kind: TYPE_SCRIPT,
      handle: pad,
      onClick: async () => {
        this.focused = handle;
        const position = new Vector3().setFromMatrixPosition(
          handle.background.matrixWorld,
        );
        const bound = checkExists(handle.background.geometry.boundingBox);
        this.listener.openScriptEditor(pad.pad, position, bound);
      },
      onEnter: () => {
        handle.background.fill = NEUTRAL_700_RGBA;
      },
      onLeave: () => {
        handle.background.fill = TRANSPARENT_RGBA;
      },
      background: new RoundedRect({
        fill: TRANSPARENT_RGBA,
        name: TYPE_STRING,
        radius: 2,
        size: new Vector2(0, 0),
        stroke: NEUTRAL_500_RGBA,
        z: Z_FOREGROUND,
      }),
      label: {
        label: label,
        mesh: await this.textRenderer.render(label),
      },
      value: {
        label: '',
        mesh: await TextMesh.empty(),
      },
    };
    this.objects.set(handle.background, handle);

    if (handle?.value.label !== label) {
      handle.label = {
        label: label,
        mesh: await this.textRenderer.render(label),
      };
    }
    if (handle?.value.label !== is) {
      handle.value = {
        label: is,
        mesh: await this.textRenderer.render(is, {name: TYPE_IGNORE}),
      };
    }

    const labelBound = checkExists(handle.label.mesh.geometry.boundingBox);
    const valueBound = checkExists(handle.value.mesh.geometry.boundingBox);
    const tree: ObjectTree = {
      kind: 'tree',
      direction: 'horizontal',
      gap: GRID_UNITS,
      gravity: 'center',
      grow: 1,
      children: [
        {
          kind: 'leaf',
          object: handle.label.mesh,
          basis: {
            height: labelBound.max.y - labelBound.min.y,
            width: labelBound.max.x - labelBound.min.x,
          },
        },
        {
          kind: 'tree',
          direction: 'horizontal',
          grow: 1,
          children: [
            {
              kind: 'leaf',
              object: SPACING,
              grow: 1,
              basis: {height: 0, width: GRID_UNITS},
            },
            {
              kind: 'leaf',
              object: handle.value.mesh,
              basis: {
                height: valueBound.max.y - valueBound.min.y,
                width: valueBound.max.x - valueBound.min.x,
              },
            },
            {
              kind: 'leaf',
              object: SPACING,
              grow: 1,
              basis: {
                height: 0,
                width: GRID_UNITS,
              },
            },
          ],
          insets: [
            {
              kind: 'leaf',
              object: handle.background,
              notify: size => {
                handle.background.size = new Vector2(size.width, size.height);
              },
              grow: 1,
            },
          ],
        },
      ],
    };

    return {
      tree,
      value: desired,
      widget: handle,
    };
  }

  private async patchSelectDropdownWidget(
    label: string,
    type: SelectType,
    labels: readonly string[],
    current: SelectDropdownWidget | undefined,
    desired: SelectValue | undefined,
    callback: (position: Vector3, bound: Box3) => Promise<void>,
  ): Promise<{
    widget: SelectDropdownWidget;
    tree: ObjectTree;
    value: SelectValue | undefined;
  }> {
    const handle = current ?? {
      kind: TYPE_SELECT_DROPDOWN,
      onClick: async () => {
        this.focused = handle;
        const position = new Vector3().setFromMatrixPosition(
          handle.background.matrixWorld,
        );
        const bound = checkExists(handle.background.geometry.boundingBox);
        await callback(position, bound);
      },
      onEnter: () => {
        handle.background.fill = NEUTRAL_700_RGBA;
      },
      onLeave: () => {
        handle.background.fill = TRANSPARENT_RGBA;
      },
      background: new RoundedRect({
        fill: TRANSPARENT_RGBA,
        name: TYPE_STRING,
        radius: 2,
        size: new Vector2(0, 0),
        stroke: NEUTRAL_500_RGBA,
        z: Z_FOREGROUND,
      }),
      arrow: await this.textRenderer.render('▼', {
        name: TYPE_IGNORE,
        scale: 0.6,
      }),
      label: {
        label,
        mesh: await this.textRenderer.render(label),
      },
      value: {
        label: '',
        mesh: await TextMesh.empty(),
      },
    };
    this.objects.set(handle.background, handle);

    let selected = -1;
    if (desired) {
      selected = type.options.indexOf(desired.value);
    }
    if (!type.nullable && selected < 0) {
      selected = 0;
    }

    const value =
      type.nullable && selected < 0 ? undefined : type.options[selected];
    const valueLabel =
      type.nullable && selected < 0 ? '(unset)' : labels[selected];
    if (handle?.value.label !== valueLabel) {
      handle.value = {
        label,
        mesh: await this.textRenderer.render(valueLabel, {name: TYPE_IGNORE}),
      };
    }

    const arrowBound = checkExists(handle.arrow.geometry.boundingBox);
    const labelBound = checkExists(handle.label.mesh.geometry.boundingBox);
    const valueBound = checkExists(handle.value.mesh.geometry.boundingBox);
    const tree: ObjectTree = {
      kind: 'tree',
      direction: 'horizontal',
      gravity: 'center',
      grow: 1,
      children: [
        {
          kind: 'leaf',
          object: handle.label.mesh,
          basis: {
            height: labelBound.max.y - labelBound.min.y,
            width: labelBound.max.x - labelBound.min.x,
          },
        },
        {
          kind: 'leaf',
          object: SPACING,
          basis: {height: 0, width: GRID_UNITS},
        },
        {
          kind: 'tree',
          direction: 'horizontal',
          gravity: 'center',
          grow: 1,
          children: [
            {
              kind: 'leaf',
              object: SPACING,
              basis: {height: 0, width: GRID_UNITS},
            },
            {
              kind: 'leaf',
              object: handle.value.mesh,
              basis: {
                height: valueBound.max.y - valueBound.min.y,
                width: valueBound.max.x - valueBound.min.x,
              },
              grow: 1,
            },
            {
              kind: 'leaf',
              object: SPACING,
              basis: {height: 0, width: GRID_UNITS},
            },
            {
              kind: 'leaf',
              object: handle.arrow,
              basis: {
                height: arrowBound.max.y - arrowBound.min.y,
                width: arrowBound.max.x - arrowBound.min.x,
              },
            },
            {
              kind: 'leaf',
              object: SPACING,
              basis: {height: 0, width: GRID_UNITS},
            },
          ],
          insets: [
            {
              kind: 'leaf',
              object: handle.background,
              notify: size => {
                handle.background.size = new Vector2(size.width, size.height);
              },
              grow: 1,
            },
          ],
        },
      ],
    };

    return {
      tree,
      value: value
        ? {
            kind: 'select',
            value,
          }
        : undefined,
      widget: handle,
    };
  }

  private async patchSelectRadioWidget(
    type: SelectType,
    current: SelectRadioWidget | undefined,
    desired: SelectValue | undefined,
    callback: (value: SelectValue | undefined) => Promise<void>,
  ): Promise<{
    widget: SelectRadioWidget;
    tree: ObjectTree;
    value: SelectValue | undefined;
  }> {
    const handle = current ?? {
      kind: TYPE_SELECT_RADIO_GROUP,
      type,
      options: [],
    };

    const tree: ObjectTree = {
      kind: 'tree',
      direction: 'horizontal',
      gravity: 'center',
      grow: 1,
      children: [],
      insets: [],
    };

    let selected = -1;
    if (desired) {
      selected = type.options.indexOf(desired.value);
    }
    if (type.nullable) {
      selected += 1;
    } else if (selected < 0) {
      selected = 0;
    }

    const buttons = handle.options;
    const options = type.nullable ? [undefined, ...type.options] : type.options;
    for (let i = 0; i < options.length; ++i) {
      const option = options[i];
      const existing = buttons[i];
      let button;
      if (!existing || option !== existing.option) {
        const background = new RoundedRect({
          fill: NEUTRAL_700_RGBA,
          name: TYPE_SELECT_RADIO,
          radius: 4,
          round:
            (i === 0 ? Corner.TOP_LEFT | Corner.BOTTOM_LEFT : 0) |
            (i === options.length - 1
              ? Corner.TOP_RIGHT | Corner.BOTTOM_RIGHT
              : 0),
          size: new Vector2(0, 0),
          stroke: NEUTRAL_700_RGBA,
          z: Z_FOREGROUND,
        });
        button = {
          kind: TYPE_SELECT_RADIO,
          onClick: async () => {
            await callback(
              option
                ? {
                    kind: 'select',
                    value: option,
                  }
                : undefined,
            );
          },
          onEnter: () => {
            const selected = background.stroke === ACCENT_700_RGBA;
            background.fill = selected ? ACCENT_600_RGBA : NEUTRAL_600_RGBA;
          },
          onLeave: () => {
            const selected = background.stroke === ACCENT_700_RGBA;
            background.fill = selected ? ACCENT_700_RGBA : NEUTRAL_700_RGBA;
          },
          option,
          mesh: await this.textRenderer.render(
            option ? capitalize(option) : '(unset)',
            {name: TYPE_IGNORE},
          ),
          background,
        } as const;
        this.objects.set(button.background, button);
        buttons[i] = button;
      } else {
        button = existing;
      }

      button.background.fill =
        i === selected ? ACCENT_700_RGBA : NEUTRAL_700_RGBA;
      button.background.stroke =
        i === selected ? ACCENT_700_RGBA : NEUTRAL_700_RGBA;

      const bound = checkExists(button.mesh.geometry.boundingBox);
      checkExists(tree.children).push({
        kind: 'tree',
        basis: {height: 0, width: 0},
        direction: 'horizontal',
        gravity: 'center',
        grow: 1,
        children: [
          {
            kind: 'leaf',
            object: button.background,
            basis: {
              height: bound.max.y - bound.min.y + GRID_UNITS,
              width: bound.max.x - bound.min.x + 4 * GRID_UNITS,
            },
            grow: 1,
            notify: size => {
              button.background.size = new Vector2(size.width, size.height);
            },
          },
        ],
        insets: [
          {
            kind: 'leaf',
            object: button.mesh,
            basis: {height: 0, width: 0},
            grow: 0,
          },
        ],
      });
    }
    buttons.length = options.length;
    handle.options = buttons;

    const value = options[selected];
    return {
      tree,
      value: value
        ? {
            kind: 'select',
            value,
          }
        : undefined,
      widget: handle,
    };
  }

  private async patchStringWidget(
    label: string,
    type: StringType,
    current: StringWidget | undefined,
    desired: StringValue | undefined,
    onClick: (position: Vector3, bound: Box3) => Promise<void>,
    onSave: (value: string) => Promise<void>,
  ): Promise<{
    widget: StringWidget;
    tree: ObjectTree;
    value: StringValue;
  }> {
    const is = desired?.value ?? '';
    const handle = current ?? {
      kind: TYPE_STRING,
      onClick: async () => {
        this.focused = handle;
        const position = new Vector3().setFromMatrixPosition(
          handle.background.matrixWorld,
        );
        const bound = checkExists(handle.background.geometry.boundingBox);
        await onClick(position, bound);
      },
      onEnter: () => {
        handle.background.fill = NEUTRAL_700_RGBA;
      },
      onLeave: () => {
        handle.background.fill = TRANSPARENT_RGBA;
      },
      onSave,
      background: new RoundedRect({
        fill: TRANSPARENT_RGBA,
        name: TYPE_STRING,
        radius: 2,
        size: new Vector2(0, 0),
        stroke: NEUTRAL_500_RGBA,
        z: Z_FOREGROUND,
      }),
      label: {
        label: '',
        mesh: await TextMesh.empty(),
      },
      value: {
        label: '',
        mesh: await TextMesh.empty(),
      },
    };
    this.objects.set(handle.background, handle);

    if (handle?.value.label !== label) {
      handle.label = {
        label: label,
        mesh: await this.textRenderer.render(label),
      };
    }
    if (handle?.value.label !== is) {
      handle.value = {
        label: is,
        mesh: await this.textRenderer.render(is, {name: TYPE_IGNORE}),
      };
    }

    const labelBound = checkExists(handle.label.mesh.geometry.boundingBox);
    const valueBound = checkExists(handle.value.mesh.geometry.boundingBox);
    const valueWidth = valueBound.max.x - valueBound.min.x;
    const tree: ObjectTree = {
      kind: 'tree',
      basis: {height: 0, width: 0},
      direction: 'horizontal',
      gravity: 'center',
      grow: 1,
      insets: [],
      children: [
        {
          kind: 'leaf',
          object: handle.label.mesh,
          basis: {
            height: labelBound.max.y - labelBound.min.y,
            width: labelBound.max.x - labelBound.min.x,
          },
          grow: 0,
        },
        {
          kind: 'leaf',
          object: SPACING,
          basis: {height: 0, width: GRID_UNITS},
          grow: 0,
        },
        {
          kind: 'tree',
          basis: {height: 0, width: 0},
          direction: 'horizontal',
          gravity: 'start',
          grow: 1,
          children: [
            {
              kind: 'leaf',
              object: SPACING,
              grow: 0,
              basis: {height: 0, width: GRID_UNITS},
            },
            {
              kind: 'leaf',
              object: handle.value.mesh,
              basis: {
                height: valueBound.max.y - valueBound.min.y,
                width: valueWidth,
              },
              grow: 0,
            },
            {
              kind: 'leaf',
              object: SPACING,
              grow: 0,
              basis: {
                height: 0,
                width: Math.max(0, 24 * GRID_UNITS - valueWidth) + GRID_UNITS,
              },
            },
          ],
          insets: [
            {
              kind: 'leaf',
              object: handle.background,
              notify: size => {
                handle.background.size = new Vector2(size.width, size.height);
              },
              basis: {height: 0, width: 0},
              grow: 1,
            },
          ],
        },
      ],
    };

    return {
      tree,
      value: {
        kind: 'string',
        value: is,
      },
      widget: handle,
    };
  }

  private async patchTableWidget(
    pad: PadHandle<'input'>,
    type: TableType,
    current: TableWidget | undefined,
    desired: TableValue | undefined,
  ): Promise<{
    widget: TableWidget;
    tree: ObjectTree;
    value: TableValue | undefined;
  }> {
    const label = pad.pad.label + ':';
    const is = `(${
      desired && desired.values.size > 0
        ? getFirstElement(desired.values.values()).length
        : 0
    } rows)`;
    const handle = current ?? {
      kind: TYPE_TABLE,
      handle: pad,
      onClick: async () => {
        this.focused = handle;
        const position = new Vector3().setFromMatrixPosition(
          handle.background.matrixWorld,
        );
        const bound = checkExists(handle.background.geometry.boundingBox);
        this.listener.openTableEditor(pad.pad, position, bound);
      },
      onEnter: () => {
        handle.background.fill = NEUTRAL_700_RGBA;
      },
      onLeave: () => {
        handle.background.fill = TRANSPARENT_RGBA;
      },
      background: new RoundedRect({
        fill: TRANSPARENT_RGBA,
        name: TYPE_STRING,
        radius: 2,
        size: new Vector2(0, 0),
        stroke: NEUTRAL_500_RGBA,
        z: Z_FOREGROUND,
      }),
      label: {
        label: label,
        mesh: await this.textRenderer.render(label),
      },
      value: {
        label: '',
        mesh: await TextMesh.empty(),
      },
    };
    this.objects.set(handle.background, handle);

    if (handle?.value.label !== label) {
      handle.label = {
        label: label,
        mesh: await this.textRenderer.render(label),
      };
    }
    if (handle?.value.label !== is) {
      handle.value = {
        label: is,
        mesh: await this.textRenderer.render(is, {name: TYPE_IGNORE}),
      };
    }

    const labelBound = checkExists(handle.label.mesh.geometry.boundingBox);
    const valueBound = checkExists(handle.value.mesh.geometry.boundingBox);
    const tree: ObjectTree = {
      kind: 'tree',
      basis: {height: 0, width: 0},
      direction: 'horizontal',
      gravity: 'center',
      grow: 1,
      insets: [],
      children: [
        {
          kind: 'leaf',
          object: handle.label.mesh,
          basis: {
            height: labelBound.max.y - labelBound.min.y,
            width: labelBound.max.x - labelBound.min.x,
          },
          grow: 0,
        },
        {
          kind: 'leaf',
          object: SPACING,
          basis: {height: 0, width: GRID_UNITS},
          grow: 0,
        },
        {
          kind: 'tree',
          basis: {height: 0, width: 0},
          direction: 'horizontal',
          gravity: 'start',
          grow: 1,
          children: [
            {
              kind: 'leaf',
              object: SPACING,
              grow: 1,
              basis: {height: 0, width: GRID_UNITS},
            },
            {
              kind: 'leaf',
              object: handle.value.mesh,
              basis: {
                height: valueBound.max.y - valueBound.min.y,
                width: valueBound.max.x - valueBound.min.x,
              },
              grow: 0,
            },
            {
              kind: 'leaf',
              object: SPACING,
              grow: 1,
              basis: {
                height: 0,
                width: GRID_UNITS,
              },
            },
          ],
          insets: [
            {
              kind: 'leaf',
              object: handle.background,
              notify: size => {
                handle.background.size = new Vector2(size.width, size.height);
              },
              basis: {height: 0, width: 0},
              grow: 1,
            },
          ],
        },
      ],
    };

    return {
      tree,
      value: desired,
      widget: handle,
    };
  }

  private async patchTemperatureWidget(
    label: string,
    type: TemperatureType,
    current: TemperatureWidget | undefined,
    desired: TemperatureValue | undefined,
    onClick: (position: Vector3, bound: Box3) => Promise<void>,
    onSave: (value: string) => Promise<void>,
  ): Promise<{
    widget: TemperatureWidget;
    tree: ObjectTree;
    value: TemperatureValue | undefined;
  }> {
    const is = desired?.numberC;
    const handle: TemperatureWidget = current ?? {
      kind: TYPE_TEMPERATURE,
      number: undefined as any,
      unit: {
        label: '°C',
        mesh: await this.textRenderer.render('°C'),
      },
    };
    // We do this outside the block above to pick up string changes
    handle.number = await this.patchStringWidget(
      label,
      {kind: 'string'},
      current?.number?.widget,
      is !== undefined ? {kind: 'string', value: String(is)} : undefined,
      onClick,
      onSave,
    );

    const unitBound = checkExists(handle.unit.mesh.geometry.boundingBox);
    const tree: ObjectTree = {
      kind: 'tree',
      basis: {height: 0, width: 0},
      direction: 'horizontal',
      gravity: 'center',
      grow: 1,
      insets: [],
      children: [
        handle.number.tree,
        {
          kind: 'leaf',
          object: handle.unit.mesh,
          basis: {
            height: unitBound.max.y - unitBound.min.y,
            width: unitBound.max.x - unitBound.min.x,
          },
          grow: 0,
        },
      ],
    };

    return {
      tree,
      value:
        is !== undefined
          ? {
              kind: 'temperature',
              numberC: is,
            }
          : undefined,
      widget: handle,
    };
  }

  private async patchVolumeWidget(
    label: string,
    type: VolumeType,
    current: VolumeWidget | undefined,
    desired: VolumeValue | undefined,
    numberClick: (position: Vector3, bound: Box3) => Promise<void>,
    numberSave: (value: string) => Promise<void>,
    unitClick: (position: Vector3, bound: Box3) => Promise<void>,
  ): Promise<{
    widget: VolumeWidget;
    tree: ObjectTree;
    value: VolumeValue | undefined;
  }> {
    const handle: VolumeWidget = current ?? {
      kind: TYPE_VOLUME,
      number: undefined as any,
      unit: undefined as any,
    };
    const value: VolumeValue = desired ?? {
      kind: 'volume',
      numberNl: 10_000,
      preferredUnit: 'ul',
    };
    const displayed = nlToUnit(value.numberNl, value.preferredUnit);
    handle.number = await this.patchStringWidget(
      label,
      {kind: 'string'},
      current?.number?.widget,
      {
        kind: 'string',
        value: serialize({
          kind: 'float',
          number: displayed,
          preferredDisplay: 'raw',
        }),
      },
      numberClick,
      numberSave,
    );
    handle.unit = await this.patchSelectDropdownWidget(
      '',
      {
        kind: 'select',
        nullable: false,
        // TODO(april): yolo?
        options: VOLUME_IDS,
      },
      VOLUME_LABELS,
      // TODO(april): something spooky afoot. handle.unit.widget should be okay but actually
      // unit can be undefined due to the typecast.
      current?.unit?.widget,
      {kind: 'select', value: value.preferredUnit},
      unitClick,
    );

    const tree: ObjectTree = {
      kind: 'tree',
      basis: {height: 0, width: 0},
      direction: 'horizontal',
      gravity: 'start',
      grow: 1,
      children: [
        handle.number.tree,
        {
          kind: 'leaf',
          object: SPACING,
          basis: {height: GRID_UNITS, width: 0},
          grow: 0,
        },
        handle.unit.tree,
      ],
      insets: [],
    };

    return {
      tree,
      value,
      widget: handle,
    };
  }

  private async patchMaterialWidget(
    label: string,
    type: MaterialType,
    current: MaterialWidget | undefined,
    desired: MaterialValue | undefined,
    contentCallback: (position: Vector3, bound: Box3) => Promise<void>,
  ): Promise<{
    widget: MaterialWidget;
    tree: ObjectTree;
    value: MaterialValue | undefined;
  }> {
    const handle: MaterialWidget = current ?? {
      kind: TYPE_MATERIAL,
      onClick: async () => {},
      material: undefined as any,
    };
    handle.material = await this.patchSelectDropdownWidget(
      `${label}:`,
      {
        kind: 'select',
        nullable: true,
        options: desired ? [desired.id] : [],
      },
      desired ? [desired.label] : [],
      // TODO(april): something spooky afoot. handle.material.widget should be okay but actually
      // material can be undefined due to the typecast.
      current?.material?.widget,
      desired ? {kind: 'select', value: desired.id} : undefined,
      contentCallback,
    );

    const tree: ObjectTree = {
      kind: 'tree',
      basis: {height: 0, width: 0},
      direction: 'vertical',
      gravity: 'start',
      grow: 1,
      children: [handle.material.tree],
      insets: [],
    };

    return {
      tree,
      value: desired?.kind === 'material' ? desired : undefined,
      widget: handle,
    };
  }

  query(object: Object3D): ObjectHandle | undefined {
    return this.objects.get(object);
  }
}
